/* eslint-disable import/prefer-default-export */
import _ from 'lodash';

import { isValidInteger, safeJSONParse } from 'global-services/utils/OhsUtility';
import { getWorkplaceIds } from 'user/OhsUserServices';
import { OhsUser } from 'user/OhsUser';
import OhsUserLocalServices from 'user/OhsUserLocalServices';

import { formatBinaryStringToArray } from '../OhsBinaryChecker';
import { OhsFormattedParamList, OhsListParams, ParamTypes } from '../OhsRegisterModels';
import { globalFilterDefaults } from '../OhsRegisterConstants';
import { normalizeSearchParams } from './normalizeSearchParams';

// builds new filter param object
const buildNewURLParam = (
  params: URLSearchParams,
  updates: Record<string, any>
): URLSearchParams => {
  const newParams = new URLSearchParams(params.toString());

  Object.keys(updates).forEach((key) => {
    const value = updates[key];
    const newValue = typeof value === 'string' ? value : JSON.stringify(value);
    newParams.set(key, newValue);
  });

  return newParams;
};

// format link parameters readable on all listing
export const formatListParamsToPayload = (
  searchParams: URLSearchParams,
  props?: {
    defaultFilter?: any;
    moduleOptions?: string[];
    categoryOptions?: string[];
    subCategoryOptions?: string[];
    severityOptions?: string[];
    statusOptions?: string[];
    updates?: any;
  }
): OhsFormattedParamList => {
  let newSearchParams = searchParams;
  if (props?.updates) {
    newSearchParams = buildNewURLParam(newSearchParams, props?.updates);
  }
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;

  // get params and set all keys to lowerCase for consistency
  const params: OhsListParams = normalizeSearchParams(newSearchParams);

  // ============================== FUNCS ==============================
  // use param value as primary, default value from module defaults, then global defaults
  const returnValue = (key: keyof OhsListParams) => {
    const moduleDefault = props?.defaultFilter?.[key];
    const value = params?.[key];
    let newValue = value ?? moduleDefault;
    const globalDefault = _.get(globalFilterDefaults, key) ?? undefined;

    // special handling on some fields
    if (newValue) {
      // for handling default that are string
      if (
        key === ParamTypes.Sort ||
        key === ParamTypes.DateCompleted ||
        key === ParamTypes.DateSignedoff ||
        key === ParamTypes.DateCreated ||
        key === ParamTypes.DateIdentified ||
        key === ParamTypes.DateOccurred ||
        key === ParamTypes.DateIssued ||
        key === ParamTypes.DateExpiry
      )
        newValue = safeJSONParse(newValue);

      // if date due is date format, parse
      if (key === ParamTypes.DateDue) {
        if (newValue.includes('start') && newValue.includes('end'))
          newValue = safeJSONParse(newValue);
      }
    }
    return newValue ?? globalDefault;
  };
  const returnBooleanValue = (key: keyof OhsListParams) => {
    const moduleDefault = props?.defaultFilter?.[key];
    const value = params?.[key];
    const newValue = typeof value === 'string' ? params?.[key] === '1' : null;

    const globalDefault = _.get(globalFilterDefaults, key) ?? undefined;

    return newValue ?? moduleDefault ?? globalDefault;
  };

  // ============================== BASIC PARAMS ==============================
  const archived = returnBooleanValue(ParamTypes.Archived);
  const confidential = returnBooleanValue(ParamTypes.Confidential);
  const showSignedOff = returnBooleanValue(ParamTypes.ShowSignedOff);
  const sort = returnValue(ParamTypes.Sort);
  const tab = returnValue(ParamTypes.Tab);
  const folderView = returnBooleanValue(ParamTypes.FolderView);
  const skipAllocatedRecords = returnBooleanValue(ParamTypes.SkipAllocatedRecords);
  const dateDue = returnValue(ParamTypes.DateDue);
  const dateDueType = returnValue(ParamTypes.DateDueType);
  const dateCompleted = returnValue(ParamTypes.DateCompleted);
  const dateSignedoff = returnValue(ParamTypes.DateSignedoff);
  const dateCreated = returnValue(ParamTypes.DateCreated);
  const dateOccurred = returnValue(ParamTypes.DateOccurred);
  const dateIdentified = returnValue(ParamTypes.DateIdentified);
  const dateIssued = returnValue(ParamTypes.DateIssued);
  const dateExpiry = returnValue(ParamTypes.DateExpiry);
  const allocatedToOrg = returnBooleanValue(ParamTypes.AllocatedToOrg);
  const allocatedToWorkplace = returnBooleanValue(ParamTypes.AllocatedToWorkplace);
  const viewPreset = returnValue(ParamTypes.ViewPreset);
  const workplaceOwned = returnBooleanValue(ParamTypes.WorkplaceOwned);
  const notAllocated = returnBooleanValue(ParamTypes.NotAllocated);
  const propOrEnvDamage = returnBooleanValue(ParamTypes.PropOrEnvDamage);
  const leave = returnBooleanValue(ParamTypes.Leave);
  const includeTierNull = returnBooleanValue(ParamTypes.IncludeTierNull);
  const reviewScheduled = returnBooleanValue(ParamTypes.ReviewScheduled);
  const copied = returnBooleanValue(ParamTypes.Copied);
  const copiedVersionUpdated = returnBooleanValue(ParamTypes.CopiedVersionUpdated);
  const notCopied = returnBooleanValue(ParamTypes.NotCopied);
  const category = returnValue(ParamTypes.Category);
  const subcategory = returnValue(ParamTypes.Subcategory);

  // ============================== SEARCH ==============================
  // searchKey & substringSearch & skipOrgRecords
  const searchKey = returnValue(ParamTypes.SearchKey);
  const hasSearch = !!searchKey;
  let substringSearch: any;
  if (hasSearch) {
    substringSearch = returnBooleanValue(ParamTypes.SubstringSearch);
  }
  const skipOrgRecords = returnBooleanValue(ParamTypes.SkipOrgRecords);

  // ============================== PAGINATION ==============================
  // currentPage & next
  let page = isValidInteger(params[ParamTypes.Page], true)
    ? (_.toInteger(params[ParamTypes.Page]) as number)
    : globalFilterDefaults?.[ParamTypes.Page];
  const next = returnValue(ParamTypes.Next);
  // if no next is provided but current page exceeds 10, reset to 1
  if (!next && page > 10) page = 1;

  // ============================== BINARIES ==============================
  // workplace require hash and options before returning any value
  let workplaces =
    props?.defaultFilter?.workplaces ?? globalFilterDefaults?.[ParamTypes.Workplaces];
  if (params?.[ParamTypes.Workplaces] && params?.[ParamTypes.WorkplacesHash]) {
    const workplaceOptions = getWorkplaceIds(user?.configs);
    workplaces = formatBinaryStringToArray({
      param: params?.[ParamTypes.Workplaces],
      hash: params?.[ParamTypes.WorkplacesHash],
      options: workplaceOptions,
      fallback: workplaceOptions,
    });
  }

  // modules require hash and options before returning any value
  let modules = props?.defaultFilter?.modules ?? globalFilterDefaults?.[ParamTypes.Modules];
  if (params?.[ParamTypes.Modules] && params?.[ParamTypes.ModulesHash] && props?.moduleOptions) {
    modules = formatBinaryStringToArray({
      param: params?.[ParamTypes.Modules],
      hash: params?.[ParamTypes.ModulesHash],
      options: props?.moduleOptions,
      fallback: [],
    });
  }

  // severities require hash and options before returning any value
  let severities =
    props?.defaultFilter?.severities ?? globalFilterDefaults?.[ParamTypes.Severities];
  if (
    params?.[ParamTypes.Severities] &&
    params?.[ParamTypes.SeveritiesHash] &&
    props?.severityOptions
  ) {
    severities = formatBinaryStringToArray({
      param: params?.[ParamTypes.Severities],
      hash: params?.[ParamTypes.SeveritiesHash],
      options: props?.severityOptions,
      fallback: [],
    });
  }

  // categories require hash and options before returning any value
  let categories =
    props?.defaultFilter?.categories ?? globalFilterDefaults?.[ParamTypes.Categories];
  if (
    params?.[ParamTypes.Categories] &&
    params?.[ParamTypes.CategoriesHash] &&
    props?.categoryOptions
  ) {
    categories = formatBinaryStringToArray({
      param: params?.[ParamTypes.Categories],
      hash: params?.[ParamTypes.CategoriesHash],
      options: props?.categoryOptions,
      fallback: [],
    });
  }

  let subcategories =
    props?.defaultFilter?.subcategories ?? globalFilterDefaults?.[ParamTypes.SubCategories];
  if (
    params?.[ParamTypes.SubCategories] &&
    params?.[ParamTypes.SubCategoriesHash] &&
    props?.subCategoryOptions
  ) {
    subcategories = formatBinaryStringToArray({
      param: params?.[ParamTypes.SubCategories],
      hash: params?.[ParamTypes.SubCategoriesHash],
      options: props?.subCategoryOptions,
      fallback: [],
    });
  }

  let status = props?.defaultFilter?.status ?? globalFilterDefaults?.[ParamTypes.Status];
  if (params?.[ParamTypes.Status] && params?.[ParamTypes.StatusHash] && props?.statusOptions) {
    status = formatBinaryStringToArray({
      param: params?.[ParamTypes.Status],
      hash: params?.[ParamTypes.StatusHash],
      options: props?.statusOptions,
      fallback: [],
    });
  }

  return {
    archived,
    confidential,
    showSignedOff,
    sort,
    tab,
    folderView,

    searchKey,
    hasSearch,
    substringSearch,
    skipOrgRecords,
    skipAllocatedRecords,
    dateDue,
    dateDueType,
    dateCompleted,
    dateSignedoff,
    dateCreated,
    dateIdentified,
    dateOccurred,
    dateIssued,
    dateExpiry,
    reviewScheduled,
    copied,
    copiedVersionUpdated,
    notCopied,
    category,
    subcategory,

    page,
    next,

    severities,
    categories,
    subcategories,
    workplaces,
    modules,
    status,
    allocatedToOrg,
    allocatedToWorkplace,
    viewPreset,
    workplaceOwned,
    notAllocated,
    propOrEnvDamage,
    leave,
    includeTierNull,
  };
};
